import React from 'react';
import { I18n } from '@aws-amplify/core';
import { useParams } from 'react-router-dom';
import { Row, Col, Form } from 'antd';

import { PinInputField } from './PinInputField';
import { usePinInput } from './hooks/usePinInput';
import { Title, Text } from '@/components/Typography';
import { GeneratePinButton } from './GeneratePinButton';
import { usePinGeneration } from './hooks/usePinGeneration';
import { useResponsive } from '../SurveyResponse/hooks/useResponsive';
import { useGetValidateSurveyToken } from '@/hooks/useGetValidateSurveyToken';
import {
	InputWrapperPrimary,
	MessageContainer,
	PinInfoMessage,
	AnonymousCol,
	FormContent,
	StyledCard,
	CodeInput,
	InputRow
} from '../styles';

interface PinCardProps {
	isAnonymous: boolean;
}

interface RouteParams {
	token: string;
}

export function PinCard({ isAnonymous }: Readonly<PinCardProps>) {
	const { isMobile } = useResponsive();
	const { token } = useParams<RouteParams>();
	const formInstance = Form.useFormInstance();

	const { pinValues, pinInputRefs, handlePinChange, handleKeyDown, handlePaste, fillPinAutomatically } =
		usePinInput();

	const { data: surveyData } = useGetValidateSurveyToken({
		token_survey: token || ''
	});

	const { pinMessage, isGeneratingPin, isRequestLimited, handleGeneratePin } = usePinGeneration({
		fillPinAutomatically,
		formInstance,
		isAnonymous,
		surveyData,
		token
	});

	return (
		<StyledCard>
			<Title level={3}>Hello! Welcome to your psychosocial evaluation.</Title>
			<Text>
				{!isAnonymous
					? I18n.get('Access your test through your credentials.')
					: I18n.get('To access your test, generate an automatic PIN.')}
			</Text>

			<FormContent>
				<div className="form-group">
					<Row gutter={[24, 16]}>
						<Col xs={24} sm={24} md={12} lg={12} xl={12} hidden={isAnonymous}>
							<InputRow>
								<InputWrapperPrimary>
									<Form.Item
										name="employee_code"
										label={I18n.get('Employee code')}
										rules={[
											{
												required: !isAnonymous,
												message: I18n.get('Please enter employee code')
											}
										]}
									>
										<CodeInput placeholder={I18n.get('Employee code')} />
									</Form.Item>
								</InputWrapperPrimary>

								<GeneratePinButton
									onClick={handleGeneratePin}
									isLoading={isGeneratingPin}
									isDisabled={isRequestLimited}
								/>
							</InputRow>
							{pinMessage && (
								<MessageContainer>
									<PinInfoMessage>{pinMessage}</PinInfoMessage>
								</MessageContainer>
							)}
						</Col>
						<AnonymousCol
							style={{ paddingTop: isMobile ? 0 : 25 }}
							hidden={!isAnonymous}
							sm={24}
							md={12}
							lg={12}
							xl={12}
							xs={24}
						>
							<GeneratePinButton
								onClick={handleGeneratePin}
								isLoading={isGeneratingPin}
								isDisabled={isRequestLimited}
							/>
						</AnonymousCol>
						<Col xs={24} sm={24} md={12} lg={12} xl={12}>
							<PinInputField
								anonymous={isAnonymous}
								pinValues={pinValues}
								pinInputRefs={pinInputRefs}
								onPinChange={handlePinChange}
								onKeyDown={handleKeyDown}
								onPaste={handlePaste}
							/>
						</Col>
					</Row>
				</div>
			</FormContent>
		</StyledCard>
	);
}
